import { AssistantMarkdownContent } from "@/components/chat/AssistantMarkdownContent";
import { assistantTextForChatDisplay } from "@/components/assistant/stripWriteApprovalProposalTable";
import { chatSendButtonClassName } from "@/components/chat/chat-controls";
import {
  CHAT_COMPOSER_MAX_HEIGHT_PX,
  CHAT_COMPOSER_MIN_HEIGHT_PX,
  resizeComposerTextarea,
} from "@/components/chat/resizeComposerTextarea";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { IconButton } from "@doculegis/shared";
import { getToolName, isToolUIPart, type UIMessage } from "ai";
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Globe,
  Loader2,
  Paperclip,
  Plus,
  Send,
  Settings2,
  Sparkles,
  User,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { SupervisorMessageActions } from "./SupervisorMessageActions";
import {
  inspectWebResearchMessage,
  isIncompleteResearchAnswer,
  SUBSTANTIAL_RESEARCH_ANSWER_CHARS,
  type WebResearchSource,
} from "../lib/web-research-message";
import { inspectKnowledgeSearchMessage } from "../lib/knowledge-search-message";
import {
  documentIdFromFileUrl,
  splitUserTextForAttachments,
  type ChatAttachmentRef,
} from "../lib/chat-attachment-marker";
import {
  clearComposerDraft,
  composerDraftKey,
  dataUrlToBlob,
  draftToSnapshot,
  loadComposerDraft,
  saveComposerDraft,
} from "../lib/composer-draft-store";
import { resolveGeneralChatDocumentId } from "../lib/resolve-general-chat-document";
import {
  knowledgeScopeHeaderSummary,
  resolveSelectedLibraries,
  type KnowledgeScopeLibraryOption,
  type KnowledgeScopeOptions,
  type KnowledgeScopeSelection,
} from "../lib/knowledge-scope";
import { fetchKnowledgeScopeOptions } from "../lib/knowledge-scope-api";
import {
  ComposerPlusMenu,
  type ComposerPlusMenuView,
} from "./ComposerPlusMenu";
import { MessageSourcesFooter } from "@/apps/rag/components/MessageSourcesFooter";
import { CitationPanel } from "@/apps/rag/components/CitationPanel";
import {
  DocumentViewerPanel,
  type ActiveDocumentView,
} from "@/apps/rag/components/DocumentViewerPanel";
import type { OnOpenCitationDocument } from "@/apps/rag/lib/open-citation-document";
import type { RagCitation } from "@/apps/rag/types";
import { reviewIdInMessage } from "../lib/latest-review-canvas";
import { isSupervisorWriteToolName } from "../specialists";
import {
  SLASH_COMMANDS,
  applySlashCommand,
  commandMatches,
  isSlashCommandEnabled,
  slashQuery,
  slashRest,
  type SlashCommand,
  type SlashCommandId,
} from "../lib/slash-commands";

const slashBadgeClassName =
  "inline-flex max-w-full items-center gap-1 rounded-lg border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-xs font-medium text-[#7c3aed] dark:border-violet-400/40 dark:bg-violet-500/20 dark:text-violet-200";
import { useSupervisorApproval } from "./SupervisorApprovalContext";
import { useReviewQuestion } from "./ReviewQuestionContext";
import {
  ReviewProgressTool,
  ReviewQuestionCard,
  ReviewStepLog,
  SupervisorWriteTool,
} from "./SupervisorToolRegistrations";

const ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export type SupervisorAttachment = {
  name: string;
  mediaType: string;
  dataUrl: string;
};

function fileAttachmentsFromParts(message: UIMessage): ChatAttachmentRef[] {
  if (!Array.isArray(message.parts)) return [];
  return message.parts.flatMap((part) => {
    if (part.type !== "file" || typeof part.filename !== "string" || !part.filename.length) {
      return [];
    }
    const url = "url" in part && typeof part.url === "string" ? part.url : undefined;
    return [{ filename: part.filename, documentId: documentIdFromFileUrl(url) }];
  });
}

function textParts(message: UIMessage): string[] {
  if (!Array.isArray(message.parts)) return [];
  return message.parts.flatMap((part) =>
    part.type === "text" && typeof part.text === "string" && part.text.trim().length > 0 ? [part.text] : []
  );
}

function userMessageAttachments(message: UIMessage): {
  attachments: ChatAttachmentRef[];
  visibleTexts: string[];
} {
  const fromParts = fileAttachmentsFromParts(message);
  const { attachments: fromText, visibleTexts } = splitUserTextForAttachments(textParts(message));
  const seen = new Set<string>();
  const attachments: ChatAttachmentRef[] = [];
  for (const item of [...fromParts, ...fromText]) {
    const key = `${item.filename}\0${item.documentId ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    attachments.push(item);
  }
  return { attachments, visibleTexts };
}

function toolPartMeta(part: UIMessage["parts"][number]) {
  if (isToolUIPart(part)) {
    return {
      name: getToolName(part),
      toolCallId: part.toolCallId,
      args:
        part.input && typeof part.input === "object"
          ? (part.input as Record<string, unknown>)
          : {},
      result: "output" in part ? part.output : undefined,
      isError: part.state === "output-error",
    };
  }
  const record = part as {
    type?: string;
    toolName?: string;
    toolCallId?: string;
    input?: unknown;
    args?: unknown;
    output?: unknown;
    result?: unknown;
    state?: string;
  };
  const type = typeof record.type === "string" ? record.type : "";
  const name =
    typeof record.toolName === "string"
      ? record.toolName
      : type.startsWith("tool-")
        ? type.slice("tool-".length)
        : "";
  if (!name || !record.toolCallId) return null;
  const args = record.input ?? record.args;
  return {
    name,
    toolCallId: record.toolCallId,
    args: args && typeof args === "object" ? (args as Record<string, unknown>) : {},
    result: record.output ?? record.result,
    isError: record.state === "output-error",
  };
}

function questionTool(part: UIMessage["parts"][number]) {
  const meta = toolPartMeta(part);
  if (!meta) return null;
  if (meta.name !== "collect-review-inputs" && meta.name !== "collectReviewInputs") return null;
  return {
    toolCallId: meta.toolCallId,
    args: meta.args,
    result: meta.result,
  };
}

function writeToolPart(part: UIMessage["parts"][number]) {
  const meta = toolPartMeta(part);
  if (!meta || !isSupervisorWriteToolName(meta.name)) return null;
  return meta;
}

function reviewProgressToolPart(part: UIMessage["parts"][number]) {
  const meta = toolPartMeta(part);
  if (!meta) return null;
  if (meta.name !== "start-contract-review" && meta.name !== "startContractReview") return null;
  return meta;
}

function UserMessageContent({
  attachments,
  texts,
  textKeyPrefix,
  onOpenAttachment,
  resolvingNames,
}: {
  attachments: ChatAttachmentRef[];
  texts: string[];
  textKeyPrefix: string;
  onOpenAttachment?: (attachment: ChatAttachmentRef) => void;
  resolvingNames?: Set<string>;
}) {
  return (
    <div className="min-w-0 rounded-2xl rounded-tr-sm border border-[var(--bubble-user-border)] bg-[var(--bubble-user-bg)] px-4 py-3 text-[13px] leading-relaxed text-[var(--bubble-user-fg)] dark:border-transparent dark:bg-primary dark:text-white">
      {attachments.length > 0 ? (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {attachments.map((file) => {
            const resolving = resolvingNames?.has(file.filename) ?? false;
            const canOpen = Boolean(onOpenAttachment && file.documentId);
            const chipClassName =
              "inline-flex min-w-0 max-w-full items-center gap-1 rounded-lg border border-[rgba(15,23,42,0.08)] bg-white px-2 py-0.5 text-xs dark:border-white/30 dark:bg-white/15";
            if (canOpen || resolving) {
              return (
                <button
                  key={`${file.filename}-${file.documentId ?? "legacy"}`}
                  type="button"
                  className={cn(
                    chipClassName,
                    canOpen && "cursor-pointer hover:bg-white/90 dark:hover:bg-white/25",
                    resolving && "opacity-70",
                  )}
                  onClick={() => {
                    if (canOpen) onOpenAttachment?.(file);
                  }}
                  aria-label={canOpen ? `View ${file.filename}` : file.filename}
                  disabled={!canOpen}
                >
                  <Paperclip className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 break-all">{file.filename}</span>
                  {resolving ? (
                    <Loader2 className="h-3 w-3 shrink-0 animate-spin opacity-70" aria-hidden />
                  ) : null}
                </button>
              );
            }
            return (
              <span
                key={`${file.filename}-${file.documentId ?? "legacy"}`}
                className={chipClassName}
              >
                <Paperclip className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                <span className="min-w-0 break-all">{file.filename}</span>
              </span>
            );
          })}
        </div>
      ) : null}
      {texts.map((text, index) => (
        <p key={`${textKeyPrefix}-text-${index}`} className="whitespace-pre-wrap break-words">
          {text}
        </p>
      ))}
    </div>
  );
}

function UserBubble({
  message,
  onOpenAttachment,
}: {
  message: UIMessage;
  onOpenAttachment?: (attachment: ChatAttachmentRef) => void;
}) {
  const { attachments, visibleTexts } = userMessageAttachments(message);
  const [resolvedIds, setResolvedIds] = useState<Record<string, string>>({});
  const [resolvingNames, setResolvingNames] = useState<Set<string>>(() => new Set());
  const legacyLookupKey = attachments
    .filter((item) => !item.documentId)
    .map((item) => item.filename)
    .join("\0");

  useEffect(() => {
    let cancelled = false;
    const missing = legacyLookupKey ? legacyLookupKey.split("\0").filter(Boolean) : [];
    if (missing.length === 0) return;

    void (async () => {
      for (const filename of missing) {
        if (cancelled) return;
        setResolvingNames((prev) => new Set(prev).add(filename));
        try {
          const documentId = await resolveGeneralChatDocumentId(filename);
          if (cancelled || !documentId) continue;
          setResolvedIds((prev) => (prev[filename] ? prev : { ...prev, [filename]: documentId }));
        } catch {
          // Leave chip non-openable when lookup fails.
        } finally {
          if (!cancelled) {
            setResolvingNames((prev) => {
              const next = new Set(prev);
              next.delete(filename);
              return next;
            });
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [legacyLookupKey]);

  const displayAttachments = attachments.map((item) => ({
    ...item,
    documentId: item.documentId ?? resolvedIds[item.filename],
  }));

  return (
    <div
      data-role="user"
      className="ml-auto flex min-w-0 max-w-[85%] flex-row-reverse gap-2.5"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-user-border)] bg-[var(--avatar-user-bg)] dark:border-transparent dark:bg-primary">
        <User className="h-3.5 w-3.5 text-[var(--avatar-user-fg)] dark:text-white" />
      </div>
      <div className="flex min-w-0 flex-col items-end">
        <UserMessageContent
          attachments={displayAttachments}
          texts={visibleTexts}
          textKeyPrefix={message.id}
          onOpenAttachment={onOpenAttachment}
          resolvingNames={resolvingNames}
        />
        <SupervisorMessageActions message={message} variant="user" />
      </div>
    </div>
  );
}

/** Provisional user bubble while a general attachment is still uploading/indexing. */
function PendingUserAttachBubble({ preview }: { preview: PendingAttachPreview }) {
  return (
    <div
      data-role="user"
      data-slot="pending-attach-preview"
      className="ml-auto flex min-w-0 max-w-[85%] flex-row-reverse gap-2.5"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-user-border)] bg-[var(--avatar-user-bg)] dark:border-transparent dark:bg-primary">
        <User className="h-3.5 w-3.5 text-[var(--avatar-user-fg)] dark:text-white" />
      </div>
      <div className="flex min-w-0 flex-col items-end">
        <UserMessageContent
          attachments={[{ filename: preview.fileName }]}
          texts={preview.text ? [preview.text] : []}
          textKeyPrefix="pending-attach"
        />
      </div>
    </div>
  );
}

function AssistantAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
      <Sparkles className="h-3.5 w-3.5 text-[var(--avatar-assistant-fg)] dark:text-white" />
    </div>
  );
}

const assistantBubbleClassName =
  "min-w-0 rounded-2xl rounded-tl-sm border border-[rgba(15,23,42,0.09)] bg-white px-4 py-3 text-[13px] leading-relaxed text-[#101828] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none";

const LOOKUP_STATUS = "Looking up roles and contract types";
const PROCESSING_DOCUMENT_STATUS = "Processing document";
const LEGAL_RESEARCH_LABEL = "Legal Research";
const KNOWLEDGE_SEARCH_LABEL = "Knowledge Search";
const STREAM_TAIL_WORDS = 4;

export type PendingAttachPreview = {
  fileName: string;
  text: string;
};

function KnowledgeSearchActivityLog({
  query,
  statusLabel,
  since,
  active,
}: {
  query: string | null;
  statusLabel: string | null;
  since: Date;
  active: boolean;
}) {
  const now = useNow(true);
  const elapsed = Math.max(0, Math.floor((now - since.getTime()) / 1000));
  const statusLine =
    statusLabel ??
    (query?.trim() ? `Searching for “${query.trim()}”` : "Searching your knowledge base…");

  return (
    <div className="min-w-0 space-y-2 pl-0.5" data-slot="knowledge-search-activity">
      <p className="flex items-center gap-2 text-[13px] text-[#576274] dark:text-slate-400">
        {active ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-violet-500 dark:text-violet-300" />
        ) : (
          <BookOpen className="h-3.5 w-3.5 shrink-0 text-[#576274]/70 dark:text-slate-500" />
        )}
        <span>Working for {elapsed}s</span>
      </p>
      <p className="text-[13px] leading-relaxed text-[#576274] dark:text-slate-400">{statusLine}</p>
    </div>
  );
}

function WebSourcesFooter({ sources }: { sources: WebResearchSource[] }) {
  if (sources.length === 0) return null;
  return (
    <div className="mt-2.5 border-t border-[rgba(15,23,42,0.08)] pt-2 dark:border-white/10">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-[#576274] dark:text-slate-400">
        Sources
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sources.map((source, index) => (
          <a
            key={source.url}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            title={source.domain ? `${source.title} · ${source.domain}` : source.title}
            className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.08)] bg-[var(--surface-sunken)] px-2 py-1 text-left transition-colors hover:border-[rgba(147,51,245,0.28)] dark:border-white/10 dark:bg-slate-950/30 dark:hover:border-violet-400/40"
          >
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full border border-[var(--citation-border)] bg-[var(--citation-bg)] text-[9px] font-bold leading-none text-[#7c3aed] dark:border-transparent dark:bg-violet-600 dark:text-white">
              {index + 1}
            </span>
            <span className="min-w-0 truncate text-[11px] font-medium text-[#576274] dark:text-slate-300">
              {source.title}
            </span>
            {source.domain ? (
              <span className="shrink-0 text-[10px] text-[#576274]/80 dark:text-slate-500">
                {source.domain}
              </span>
            ) : null}
            <ExternalLink className="h-3 w-3 shrink-0 text-[#576274]/70 dark:text-slate-400" />
          </a>
        ))}
      </div>
    </div>
  );
}

function WebSearchActivitySourceRow({
  source,
  index,
}: {
  source: WebResearchSource;
  index: number;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), Math.min(index, 12) * 55);
    return () => window.clearTimeout(id);
  }, [index]);

  const label = source.domain || source.title;
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      title={source.title}
      className={cn(
        "flex items-center gap-2 py-0.5 text-left transition-[opacity,transform] duration-300 ease-out",
        visible ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
      )}
    >
      <Globe className="h-3.5 w-3.5 shrink-0 text-[#576274]/80 dark:text-slate-500" />
      <span className="min-w-0 truncate text-[13px] text-[#576274] dark:text-slate-400">
        Searched {label}
      </span>
    </a>
  );
}

/** Flat ChatGPT-style activity log on the chat background (no bubble). */
function WebSearchActivityLog({
  query,
  statusLabel,
  sources,
  since,
  active,
}: {
  query: string | null;
  statusLabel: string | null;
  sources: WebResearchSource[];
  since: Date;
  active: boolean;
}) {
  const now = useNow(true);
  const elapsed = Math.max(0, Math.floor((now - since.getTime()) / 1000));
  const statusLine =
    statusLabel ??
    (query?.trim() ? `Searching for “${query.trim()}”` : "Searching the web…");

  return (
    <div className="min-w-0 space-y-2 pl-0.5" data-slot="web-search-activity">
      <p className="flex items-center gap-2 text-[13px] text-[#576274] dark:text-slate-400">
        {active ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-violet-500 dark:text-violet-300" />
        ) : (
          <Globe className="h-3.5 w-3.5 shrink-0 text-[#576274]/70 dark:text-slate-500" />
        )}
        <span>Working for {elapsed}s</span>
      </p>
      <p className="text-[13px] leading-relaxed text-[#576274] dark:text-slate-400">{statusLine}</p>
      {sources.length > 0 ? (
        <div className="flex flex-col">
          {sources.map((source, index) => (
            <WebSearchActivitySourceRow key={source.url} source={source} index={index} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function splitStreamingTail(text: string): { prefix: string; tail: string } {
  const trimmedEnd = text.trimEnd();
  const words = trimmedEnd.split(/\s+/).filter((word) => word.length > 0);
  if (words.length <= STREAM_TAIL_WORDS) {
    return { prefix: "", tail: text };
  }
  const tail = words.slice(-STREAM_TAIL_WORDS).join(" ");
  const prefix = text.slice(0, text.length - tail.length);
  return { prefix, tail };
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

function useBusySince(busy: boolean): Date {
  const sinceRef = useRef(new Date());
  const wasBusy = useRef(false);
  if (busy && !wasBusy.current) {
    sinceRef.current = new Date();
  }
  wasBusy.current = busy;
  return sinceRef.current;
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1" aria-label="Assistant is typing">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#576274]/45 dark:bg-white/35"
          style={{ animationDelay: `${index * 160}ms`, animationDuration: "1s" }}
        />
      ))}
    </span>
  );
}

function ThinkingLine({ label, since }: { label: string; since: Date }) {
  const now = useNow(true);
  const elapsed = Math.max(0, Math.floor((now - since.getTime()) / 1000));
  return (
    <p className="text-sm text-[#576274] dark:text-gray-300">
      {label} · {elapsed}s
    </p>
  );
}

function StreamingAssistantText({
  text,
  streaming,
  citations,
  activeCitationId,
  onCitationClick,
  threadId,
  onOpenDocument,
}: {
  text: string;
  streaming: boolean;
  citations?: RagCitation[];
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
}) {
  const display = assistantTextForChatDisplay(text, { hideProposalTable: true });
  if (!streaming) {
    return (
      <AssistantMarkdownContent
        content={display}
        citations={citations}
        activeCitationId={activeCitationId}
        onCitationClick={onCitationClick}
        threadId={threadId}
        onOpenDocument={onOpenDocument}
      />
    );
  }
  const { prefix, tail } = splitStreamingTail(display);
  return (
    <div>
      {prefix.trim().length > 0 ? (
        <AssistantMarkdownContent
          content={prefix}
          citations={citations}
          activeCitationId={activeCitationId}
          onCitationClick={onCitationClick}
          threadId={threadId}
          onOpenDocument={onOpenDocument}
          isStreaming
        />
      ) : null}
      {tail ? <span className="text-blue-500">{tail}</span> : null}
    </div>
  );
}

/** Collapses long post-review chat text when the Open review button is shown. */
function CollapsibleAssistantBody({
  children,
  enabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) {
  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canTruncate, setCanTruncate] = useState(false);

  useEffect(() => {
    setExpanded((prev) => (prev ? false : prev));
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCanTruncate((prev) => (prev ? false : prev));
      return;
    }
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      if (expanded) return;
      const next = el.scrollHeight > el.clientHeight + 1;
      setCanTruncate((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [enabled, expanded]);

  if (!enabled) return <>{children}</>;

  const showToggle = canTruncate || expanded;
  return (
    <div>
      <div ref={textRef} className={cn(!expanded && "line-clamp-4")}>
        {children}
      </div>
      {showToggle ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 text-sm font-medium text-primary hover:underline dark:text-violet-300"
        >
          {expanded ? "View less" : "View more"}
        </button>
      ) : null}
    </div>
  );
}

function AssistantBubble({
  message,
  busy,
  streaming,
  showStatus,
  busySince,
  closedReviewId,
  onOpenCanvas,
  onRegenerate,
  activeCitation,
  onCitationClick,
  threadId,
  onOpenDocument,
  feedbackRating = null,
  onFeedbackChange,
}: {
  message: UIMessage;
  busy: boolean;
  streaming: boolean;
  showStatus: boolean;
  busySince: Date;
  closedReviewId: string | null;
  onOpenCanvas?: () => void;
  onRegenerate?: () => void;
  activeCitation?: RagCitation | null;
  onCitationClick?: (citation: RagCitation) => void;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
  feedbackRating?: "positive" | "negative" | null;
  onFeedbackChange?: (rating: "positive" | "negative" | null) => void | Promise<void>;
}) {
  const progress = useReviewQuestion().reviewProgress;
  const { routeLabel } = useSupervisorApproval();
  const steps = showStatus && progress && progress.logs.length > 0 ? progress : null;
  const latestLog = steps ? steps.logs[steps.logs.length - 1] : null;
  const texts = textParts(message);
  const questions = Array.isArray(message.parts)
    ? message.parts.flatMap((part) => {
        const question = questionTool(part);
        return question ? [question] : [];
      })
    : [];
  const writeTools = Array.isArray(message.parts)
    ? message.parts.flatMap((part) => {
        const tool = writeToolPart(part);
        return tool ? [tool] : [];
      })
    : [];
  const reviewProgressTools = Array.isArray(message.parts)
    ? message.parts.flatMap((part) => {
        const tool = reviewProgressToolPart(part);
        return tool ? [tool] : [];
      })
    : [];
  const hasText = texts.length > 0;
  const joinedText = texts.join("\n");
  const isResearchRoute = routeLabel === LEGAL_RESEARCH_LABEL;
  const isKnowledgeRoute = routeLabel === KNOWLEDGE_SEARCH_LABEL;
  const substantialAnswer = joinedText.length >= SUBSTANTIAL_RESEARCH_ANSWER_CHARS;
  // Treat short preamble as "no answer yet" so status stays searching/preparing during research.
  const research = inspectWebResearchMessage(message, {
    hasText: substantialAnswer,
    streaming: showStatus && streaming,
    isResearchRoute: showStatus && isResearchRoute,
  });
  const knowledge = inspectKnowledgeSearchMessage(message, {
    hasText: substantialAnswer,
    streaming: showStatus && streaming,
    isKnowledgeRoute: showStatus && isKnowledgeRoute,
  });
  const incompleteResearch = isIncompleteResearchAnswer(message, {
    text: joinedText,
    streaming: showStatus && streaming,
    busy: showStatus && busy,
  });
  const researchBusy = showStatus && (streaming || busy);
  // Live research only — never mount on idle/historical messages (avoids render loops on reload).
  const showWebActivityLog =
    showStatus &&
    researchBusy &&
    !steps &&
    !substantialAnswer &&
    !isKnowledgeRoute &&
    (research.hasResearchTools || isResearchRoute || research.status !== "idle");
  const showKnowledgeActivityLog =
    showStatus &&
    researchBusy &&
    !steps &&
    !substantialAnswer &&
    (knowledge.hasKnowledgeTools || isKnowledgeRoute || knowledge.status !== "idle");
  const showActivityLog = showWebActivityLog || showKnowledgeActivityLog;
  const showDots =
    showStatus && streaming && !hasText && !latestLog && !showActivityLog;
  const showThinking =
    showStatus && !hasText && !showDots && !steps && busy && !showActivityLog;
  const showWebSources = !streaming && !isKnowledgeRoute && research.sources.length > 0;
  const showKnowledgeSources = !streaming && knowledge.citations.length > 0;

  const showBubble =
    hasText ||
    showDots ||
    showThinking ||
    incompleteResearch ||
    showWebSources ||
    showKnowledgeSources ||
    (knowledge.refused && !streaming) ||
    (showStatus && !busy && questions.length === 0 && !steps && !showActivityLog);

  const emptyReplyCopy =
    isKnowledgeRoute || knowledge.refused
      ? knowledge.refusedMessage ??
        "No knowledge-base answer was returned. Try again or refine your question."
      : isResearchRoute || incompleteResearch
        ? "No research answer was returned. Try again or refine your question."
        : "The review did not return a reply.";

  return (
    <div data-role="assistant" className="mr-auto flex max-w-[85%] gap-2.5">
      <AssistantAvatar />
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {showBubble ? (
          <div className={cn(assistantBubbleClassName, "space-y-3")}>
            <CollapsibleAssistantBody enabled={Boolean(closedReviewId) && !streaming}>
              {texts.map((text, index) => (
                <StreamingAssistantText
                  key={`${message.id}-text-${index}`}
                  text={text}
                  streaming={streaming && showStatus && index === texts.length - 1}
                  citations={knowledge.citations}
                  activeCitationId={activeCitation?.id}
                  onCitationClick={onCitationClick}
                  threadId={threadId}
                  onOpenDocument={onOpenDocument}
                />
              ))}
            </CollapsibleAssistantBody>
            {showDots ? <TypingDots /> : null}
            {showThinking ? (
              <ThinkingLine label={LOOKUP_STATUS} since={busySince} />
            ) : null}
            {(incompleteResearch ||
              knowledge.refused ||
              (showStatus && !busy && !hasText && !steps && questions.length === 0)) ? (
              <p className="text-sm text-[#576274] dark:text-slate-300">{emptyReplyCopy}</p>
            ) : null}
            {showWebSources ? <WebSourcesFooter sources={research.sources} /> : null}
            {showKnowledgeSources ? (
              <MessageSourcesFooter
                citations={knowledge.citations}
                activeCitationId={activeCitation?.id}
                onCitationClick={onCitationClick}
              />
            ) : null}
          </div>
        ) : null}
        {showWebActivityLog ? (
          <WebSearchActivityLog
            query={research.query}
            statusLabel={research.statusLabel}
            sources={research.sources}
            since={busySince}
            active={researchBusy}
          />
        ) : null}
        {showKnowledgeActivityLog ? (
          <KnowledgeSearchActivityLog
            query={knowledge.query}
            statusLabel={knowledge.statusLabel}
            since={busySince}
            active={researchBusy}
          />
        ) : null}
        {questions.map((question) => (
          <ReviewQuestionCard
            key={question.toolCallId}
            toolCallId={question.toolCallId}
            args={question.args}
            result={question.result}
          />
        ))}
        {writeTools.map((tool) => (
          <SupervisorWriteTool
            key={tool.toolCallId}
            toolName={tool.name}
            toolCallId={tool.toolCallId}
            args={tool.args}
            result={tool.result}
            isError={tool.isError}
          />
        ))}
        {reviewProgressTools.map((tool) => (
          <ReviewProgressTool key={tool.toolCallId} result={tool.result} />
        ))}
        {steps ? <ReviewStepLog progress={steps} /> : null}
        {closedReviewId ? (
          <Button type="button" variant="outline" size="sm" className="w-fit" onClick={onOpenCanvas}>
            Open review
          </Button>
        ) : null}
        {showBubble ? (
          <SupervisorMessageActions
            message={message}
            variant="assistant"
            busy={busy}
            onRegenerate={showStatus ? onRegenerate : undefined}
            feedbackRating={feedbackRating}
            onFeedbackChange={onFeedbackChange}
          />
        ) : null}
      </div>
    </div>
  );
}

const SHORT_COMPOSER_PLACEHOLDER = "Ask to review a contract…";
const NARROW_COMPOSER_WIDTH_PX = 520;

type ComposerProps = {
  busy: boolean;
  onSend: (text: string, attachment?: SupervisorAttachment) => void;
  placeholder: string;
  docked?: boolean;
  /** Flush edge-to-edge in a narrow rail (review canvas docked). */
  compact?: boolean;
  /** Conversation id (or undefined for new chat) used to persist unsent drafts. */
  draftKey?: string;
  webSearchEnabled?: boolean;
  knowledgeSearchEnabled?: boolean;
  knowledgeScopeSelection?: KnowledgeScopeSelection | null;
  selectedScopeLibraries?: KnowledgeScopeLibraryOption[];
  onWebSearchChange?: (enabled: boolean) => void;
  onKnowledgeSearchChange?: (enabled: boolean) => void;
  onKnowledgeScopeApply?: (selection: KnowledgeScopeSelection) => void;
};

function SupervisorComposer({
  busy,
  onSend,
  placeholder,
  docked = false,
  compact = false,
  draftKey,
  webSearchEnabled = false,
  knowledgeSearchEnabled = false,
  knowledgeScopeSelection = null,
  selectedScopeLibraries = [],
  onWebSearchChange,
  onKnowledgeSearchChange,
  onKnowledgeScopeApply,
}: ComposerProps) {
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState<SupervisorAttachment | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<SlashCommand | null>(null);
  const [activeCommand, setActiveCommand] = useState(0);
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null);
  const [isScopeExpanded, setIsScopeExpanded] = useState(true);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [plusMenuInitialView, setPlusMenuInitialView] =
    useState<ComposerPlusMenuView>("root");
  const [narrow, setNarrow] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(CHAT_COMPOSER_MIN_HEIGHT_PX);
  const [draftReady, setDraftReady] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const skipNextPersistRef = useRef(false);
  const storageKey = composerDraftKey(draftKey);
  const isMultiline = textareaHeight > CHAT_COMPOSER_MIN_HEIGHT_PX;

  useEffect(() => {
    let cancelled = false;
    setDraftReady(false);
    skipNextPersistRef.current = true;
    void (async () => {
      try {
        const draft = await loadComposerDraft(storageKey);
        if (cancelled) return;
        if (draft) {
          const snapshot = await draftToSnapshot(draft);
          if (cancelled) return;
          setText(snapshot.text);
          setAttachment(snapshot.attachment);
        } else {
          setText("");
          setAttachment(null);
        }
      } catch {
        if (!cancelled) {
          setText("");
          setAttachment(null);
        }
      } finally {
        if (!cancelled) setDraftReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  useEffect(() => {
    if (!draftReady) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    const handle = window.setTimeout(() => {
      void saveComposerDraft(storageKey, {
        text,
        attachment: attachment
          ? {
              name: attachment.name,
              mediaType: attachment.mediaType,
              blob: dataUrlToBlob(attachment.dataUrl, attachment.mediaType),
            }
          : null,
      }).catch(() => {
        // Draft persistence is best-effort.
      });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [attachment, draftReady, storageKey, text]);

  const syncTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const nextHeight = resizeComposerTextarea(
      textarea,
      CHAT_COMPOSER_MIN_HEIGHT_PX,
      CHAT_COMPOSER_MAX_HEIGHT_PX,
    );
    setTextareaHeight(nextHeight);
  }, []);
  const scopeSummary = knowledgeScopeHeaderSummary(selectedScopeLibraries, knowledgeScopeSelection);
  const query = selectedCommand ? null : slashQuery(text);
  const commands = SLASH_COMMANDS.filter((command) =>
    query === null ? false : commandMatches(command, query),
  );
  const menuOpen =
    query !== null && dismissedQuery !== query && !busy && !selectedCommand && commands.length > 0;
  const searchModesLocked = selectedCommand != null;
  const displayPlaceholder = selectedCommand
    ? "Add details (optional)"
    : compact || narrow
      ? SHORT_COMPOSER_PLACEHOLDER
      : placeholder;

  useEffect(() => {
    if (query === null) {
      setActiveCommand(0);
      return;
    }
    const matched = SLASH_COMMANDS.filter((command) => commandMatches(command, query));
    const firstEnabled = matched.findIndex(isSlashCommandEnabled);
    setActiveCommand(firstEnabled >= 0 ? firstEnabled : 0);
  }, [query]);

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth < NARROW_COMPOSER_WIDTH_PX;
      setNarrow((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    syncTextareaHeight();
    const id = requestAnimationFrame(() => syncTextareaHeight());
    return () => cancelAnimationFrame(id);
  }, [text, displayPlaceholder, syncTextareaHeight]);

  function selectCommand(id: SlashCommandId) {
    const command = SLASH_COMMANDS.find((entry) => entry.id === id);
    if (!command || !isSlashCommandEnabled(command)) return;
    setSelectedCommand(command);
    setText(slashRest(text));
    setDismissedQuery(null);
    onWebSearchChange?.(false);
    onKnowledgeSearchChange?.(false);
  }

  function clearSelectedCommand() {
    setSelectedCommand(null);
  }

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (menuOpen) {
      const highlighted = commands[activeCommand] ?? commands[0];
      if (highlighted && isSlashCommandEnabled(highlighted)) {
        selectCommand(highlighted.id);
        return;
      }
      const enabled = commands.find(isSlashCommandEnabled);
      if (enabled) selectCommand(enabled.id);
      return;
    }
    const trimmed = text.trim();
    if ((!trimmed && !attachment && !selectedCommand) || busy) return;
    let nextText: string;
    if (selectedCommand) {
      nextText = applySlashCommand(selectedCommand.id, trimmed);
    } else {
      nextText = trimmed || (attachment ? `Review ${attachment.name}` : "");
    }
    if (!nextText && !attachment) return;
    setText("");
    setSelectedCommand(null);
    const file = attachment ?? undefined;
    setAttachment(null);
    skipNextPersistRef.current = true;
    void clearComposerDraft(storageKey).catch(() => {
      // Ignore draft clear failures on send.
    });
    onSend(nextText, file);
  }

  function onFile(file: File | undefined) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) return;
      setAttachment({ name: file.name, mediaType: file.type || "application/octet-stream", dataUrl });
    };
    reader.readAsDataURL(file);
  }

  const canSend = !busy && (text.trim().length > 0 || Boolean(attachment) || Boolean(selectedCommand));
  const hasChips = Boolean(attachment);
  const expandedShell = hasChips || isMultiline;

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className={cn(
        "w-full",
        docked ? "px-3 py-2.5 sm:px-4" : "p-2 sm:p-2.5",
      )}
    >
      <div className="relative overflow-visible">
        {menuOpen ? (
          <ul
            className="absolute bottom-full left-0 z-20 mb-2 max-h-40 w-72 overflow-y-auto rounded-xl border border-[rgba(15,23,42,0.09)] bg-[var(--composer-bg)] py-1 shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-900"
            role="listbox"
          >
            {commands.map((command, index) => {
              const enabled = isSlashCommandEnabled(command);
              return (
                <li key={command.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === activeCommand}
                    aria-disabled={!enabled}
                    disabled={!enabled}
                    className={cn(
                      "flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left",
                      !enabled && "cursor-not-allowed opacity-45",
                      enabled &&
                        (index === activeCommand
                          ? "bg-violet-500/15 text-[#101828] dark:text-white"
                          : "text-[#576274] hover:bg-[var(--lm-hover)] dark:text-slate-300 dark:hover:bg-white/5"),
                      !enabled &&
                        index === activeCommand &&
                        "bg-[var(--lm-hover)]/60 dark:bg-white/5",
                    )}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      if (enabled) selectCommand(command.id);
                    }}
                    onMouseEnter={() => setActiveCommand(index)}
                  >
                    <span
                      className={cn(
                        "text-sm font-medium",
                        enabled
                          ? "text-[#7c3aed] dark:text-violet-300"
                          : "text-[#576274] dark:text-slate-500",
                      )}
                    >
                      /{command.id}
                    </span>
                    <span className="text-xs text-[#576274] dark:text-slate-400">{command.hint}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {knowledgeSearchEnabled ? (
          <div className="mb-2.5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setIsScopeExpanded((prev) => !prev)}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:hover:bg-white/5 dark:hover:text-slate-200"
                aria-expanded={isScopeExpanded}
                aria-label={isScopeExpanded ? "Collapse scope" : "Expand scope"}
                title={isScopeExpanded ? "Collapse scope" : "Expand scope"}
              >
                {isScopeExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
                )}
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-[#576274] dark:text-slate-400">
                  Libraries in scope
                </span>
                {!isScopeExpanded ? (
                  <span className="truncate text-[11px] normal-case tracking-normal text-[#576274] dark:text-slate-500">
                    · {scopeSummary}
                  </span>
                ) : null}
              </button>
              <IconButton
                type="button"
                size="sm"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  setPlusMenuInitialView("workspaces");
                  setPlusMenuOpen(true);
                }}
                title="Manage scope"
                aria-label="Manage scope"
              >
                <Settings2 className="h-4 w-4" />
              </IconButton>
            </div>
            {isScopeExpanded ? (
              <div className="flex flex-wrap items-center gap-2">
                {selectedScopeLibraries.length > 0 ? (
                  selectedScopeLibraries.map((library) => (
                    <span
                      key={library.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/45 bg-violet-500/15 px-2.5 py-1 text-[11px] font-medium text-violet-200"
                    >
                      <Check className="h-3 w-3" />
                      {library.name}
                    </span>
                  ))
                ) : (
                  <span className="text-[11px] text-[#576274] dark:text-slate-500">
                    No item selected
                  </span>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(event) => {
            onFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        <div
          className={cn(
            "flex w-full flex-col gap-2 overflow-visible border border-[rgba(15,23,42,0.09)] bg-[var(--composer-bg)] shadow-[var(--shadow-card)] focus-within:border-[rgba(59,130,246,0.45)] focus-within:ring-2 focus-within:ring-[rgba(59,130,246,0.15)] dark:border-white/10 dark:bg-white/5 dark:shadow-[0_8px_28px_rgba(0,0,0,0.35)] dark:focus-within:border-blue-500/50 dark:focus-within:ring-blue-500/20",
            expandedShell ? "rounded-3xl px-3 py-3" : "rounded-full px-3 py-2.5",
          )}
        >
          {hasChips ? (
            <div className="flex flex-wrap items-center gap-2">
              {attachment ? (
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.09)] bg-white px-2 py-1 text-xs text-[#101828] dark:border-white/10 dark:bg-slate-900 dark:text-slate-100">
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
                  <span className="truncate">{attachment.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${attachment.name}`}
                    className="text-[#576274] hover:text-[#101828] dark:text-slate-400 dark:hover:text-white"
                    onClick={() => setAttachment(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(
              "flex min-w-0 gap-2",
              isMultiline ? "items-end" : "items-center",
            )}
          >
            <div className="relative shrink-0 overflow-visible">
              <ComposerPlusMenu
                open={plusMenuOpen}
                onOpenChange={(next) => {
                  setPlusMenuOpen(next);
                  if (!next) setPlusMenuInitialView("root");
                }}
                initialView={plusMenuInitialView}
                disabled={busy}
                searchModesLocked={searchModesLocked}
                webSearchEnabled={webSearchEnabled}
                knowledgeSearchEnabled={knowledgeSearchEnabled}
                knowledgeScopeSelection={knowledgeScopeSelection}
                onAttachDocs={() => fileRef.current?.click()}
                onAttachFile={onFile}
                onWebSearchChange={onWebSearchChange}
                onKnowledgeSearchChange={onKnowledgeSearchChange}
                onKnowledgeScopeApply={onKnowledgeScopeApply}
              />
              <IconButton
                type="button"
                size="sm"
                disabled={busy}
                active={plusMenuOpen}
                data-composer-plus-trigger=""
                aria-expanded={plusMenuOpen}
                aria-label="Add to chat"
                title="Add to chat"
                onClick={() => {
                  if (plusMenuOpen) {
                    setPlusMenuOpen(false);
                    setPlusMenuInitialView("root");
                    return;
                  }
                  setPlusMenuInitialView("root");
                  setPlusMenuOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
              </IconButton>
            </div>
            {selectedCommand ? (
              <span className={cn(slashBadgeClassName, "max-w-[40%] shrink-0")}>
                <span className="truncate">{selectedCommand.label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${selectedCommand.label}`}
                  className="text-[#7c3aed] hover:text-[#5b21b6] dark:text-violet-200 dark:hover:text-white"
                  onClick={clearSelectedCommand}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : null}
            <Textarea
              ref={textareaRef}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                if (!event.target.value.startsWith("/")) setDismissedQuery(null);
                const nextHeight = resizeComposerTextarea(
                  event.target,
                  CHAT_COMPOSER_MIN_HEIGHT_PX,
                  CHAT_COMPOSER_MAX_HEIGHT_PX,
                );
                setTextareaHeight(nextHeight);
              }}
              onKeyDown={(event) => {
                if (menuOpen && event.key === "ArrowDown") {
                  event.preventDefault();
                  setActiveCommand((current) => (current + 1) % commands.length);
                  return;
                }
                if (menuOpen && event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveCommand((current) => (current - 1 + commands.length) % commands.length);
                  return;
                }
                if (menuOpen && event.key === "Escape") {
                  event.preventDefault();
                  setDismissedQuery(query);
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              placeholder={displayPlaceholder}
              rows={1}
              style={{ minHeight: CHAT_COMPOSER_MIN_HEIGHT_PX }}
              className="max-h-[200px] min-h-[40px] flex-1 resize-none overflow-y-hidden border-0 bg-transparent px-1 py-2 text-sm leading-5 text-[#101828] shadow-none placeholder:text-[#576274] focus-visible:ring-0 dark:border-0 dark:bg-transparent dark:text-white dark:shadow-none dark:placeholder:text-gray-500"
              disabled={busy}
            />
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send message"
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full !p-0",
                chatSendButtonClassName(canSend),
              )}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

const DEFAULT_COMPOSER_PLACEHOLDER =
  "Ask to review a contract, or manage contract types and roles";
const REVIEW_FOLLOW_UP_PLACEHOLDER = "Ask about this contract review…";

type ThreadProps = {
  messages: UIMessage[];
  busy: boolean;
  streaming: boolean;
  isEmpty: boolean;
  onSend: (text: string, attachment?: SupervisorAttachment) => void;
  onRegenerate?: () => void;
  closedReviewId?: string | null;
  onOpenCanvas?: () => void;
  /** Flush docked composer when review canvas is docked beside chat. */
  compactComposer?: boolean;
  /** Conversation id used as threadId for attachment document opens. */
  threadId?: string;
  /** Conversation id (or undefined for new chat) used to persist unsent drafts. */
  draftKey?: string;
  webSearchEnabled?: boolean;
  knowledgeSearchEnabled?: boolean;
  knowledgeScopeSelection?: KnowledgeScopeSelection | null;
  onWebSearchChange?: (enabled: boolean) => void;
  onKnowledgeSearchChange?: (enabled: boolean) => void;
  onKnowledgeScopeApply?: (selection: KnowledgeScopeSelection) => void;
  /** True while a general-chat attachment is uploading/indexing before sendMessage. */
  attachmentUploading?: boolean;
  /** Provisional user bubble content shown during attachmentUploading. */
  pendingAttachPreview?: PendingAttachPreview | null;
  /** When set, composer invites follow-ups about the finished review. */
  hasFinishedReview?: boolean;
  messageFeedback?: Record<string, "positive" | "negative">;
  onMessageFeedbackChange?: (
    messageId: string,
    rating: "positive" | "negative" | null,
  ) => void | Promise<void>;
};

export function SupervisorThread({
  messages,
  busy,
  streaming,
  isEmpty,
  onSend,
  onRegenerate,
  closedReviewId = null,
  onOpenCanvas,
  compactComposer = false,
  threadId,
  draftKey,
  webSearchEnabled = false,
  knowledgeSearchEnabled = false,
  knowledgeScopeSelection = null,
  onWebSearchChange,
  onKnowledgeSearchChange,
  onKnowledgeScopeApply,
  attachmentUploading = false,
  pendingAttachPreview = null,
  hasFinishedReview = false,
  messageFeedback = {},
  onMessageFeedbackChange,
}: ThreadProps) {
  const progress = useReviewQuestion().reviewProgress;
  const { routeLabel } = useSupervisorApproval();
  const logCount = progress?.logs.length ?? 0;
  const latestLog = logCount > 0 ? progress?.logs[logCount - 1] : null;
  const busySince = useBusySince(busy);
  const composerPlaceholder = hasFinishedReview
    ? REVIEW_FOLLOW_UP_PLACEHOLDER
    : DEFAULT_COMPOSER_PLACEHOLDER;
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeCitation, setActiveCitation] = useState<RagCitation | null>(null);
  const [activeDocument, setActiveDocument] = useState<ActiveDocumentView | null>(null);
  const [scopeOptions, setScopeOptions] = useState<KnowledgeScopeOptions | null>(null);

  const handleOpenDocument = useCallback<OnOpenCitationDocument>(
    (sourceId, { title, initialPage, sourceType, documentVersionId, highlightText }) => {
      setActiveDocument({
        ...(sourceType === "attachment"
          ? { attachmentId: sourceId, threadId }
          : { documentId: sourceId, documentVersionId }),
        title,
        initialPage,
        highlightText,
      });
    },
    [threadId],
  );

  const handleOpenAttachment = useCallback(
    (attachment: ChatAttachmentRef) => {
      if (!attachment.documentId) return;
      handleOpenDocument(attachment.documentId, {
        title: attachment.filename,
        sourceType: "document",
      });
    },
    [handleOpenDocument],
  );

  useEffect(() => {
    if (!knowledgeSearchEnabled) return;
    let cancelled = false;
    void fetchKnowledgeScopeOptions()
      .then((options) => {
        if (!cancelled) setScopeOptions(options);
      })
      .catch(() => {
        if (!cancelled) setScopeOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [knowledgeSearchEnabled]);

  const selectedScopeLibraries = useMemo(
    () => resolveSelectedLibraries(knowledgeScopeSelection, scopeOptions),
    [knowledgeScopeSelection, scopeOptions],
  );
  const lastAssistant = [...messages].reverse().find((message) => message.role === "assistant");
  const lastAssistantId = lastAssistant?.id;
  const lastAssistantTextLen = lastAssistant ? textParts(lastAssistant).join("\n").length : 0;
  const lastResearchSourceCount = lastAssistant
    ? inspectWebResearchMessage(lastAssistant, {
        streaming: streaming && Boolean(lastAssistantId),
      }).sources.length
    : 0;
  const lastKnowledgeCitationCount = lastAssistant
    ? inspectKnowledgeSearchMessage(lastAssistant, {
        streaming: streaming && Boolean(lastAssistantId),
      }).citations.length
    : 0;
  const showPendingPresence = busy && !lastAssistantId;
  const isPendingResearch = routeLabel === LEGAL_RESEARCH_LABEL;
  const isPendingKnowledge = routeLabel === KNOWLEDGE_SEARCH_LABEL;
  const showEmptyGreeting = isEmpty && !pendingAttachPreview;
  const showPendingAttachPreview = Boolean(attachmentUploading && pendingAttachPreview);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const log = viewport.querySelector("[data-slot='review-step-log']");
    if (log instanceof HTMLElement) {
      log.scrollIntoView({ block: "nearest" });
      return;
    }
    viewport.scrollTop = viewport.scrollHeight;
  }, [
    logCount,
    messages.length,
    lastAssistantTextLen,
    lastResearchSourceCount,
    lastKnowledgeCitationCount,
    streaming,
    busy,
    showPendingPresence,
  ]);

  return (
    <div className="flex min-h-0 flex-1 text-[#101828] dark:text-slate-100">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          ref={viewportRef}
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto",
            showEmptyGreeting && "justify-center px-4 py-6",
          )}
        >
          {showEmptyGreeting ? (
            <div
              data-slot="empty-state"
              className="mx-auto flex w-full max-w-2xl flex-col items-center gap-5 text-center"
            >
              <h2
                data-slot="empty-state-greeting"
                className="text-3xl font-semibold tracking-tight text-[#101828] dark:text-white"
              >
                How can I help you today?
              </h2>
              <div data-slot="empty-state-composer" className="w-full overflow-visible">
                <SupervisorComposer
                  busy={busy}
                  onSend={onSend}
                  draftKey={draftKey}
                  webSearchEnabled={webSearchEnabled}
                  knowledgeSearchEnabled={knowledgeSearchEnabled}
                  knowledgeScopeSelection={knowledgeScopeSelection}
                  selectedScopeLibraries={selectedScopeLibraries}
                  onWebSearchChange={onWebSearchChange}
                  onKnowledgeSearchChange={onKnowledgeSearchChange}
                  onKnowledgeScopeApply={onKnowledgeScopeApply}
                  placeholder={composerPlaceholder}
                />
              </div>
            </div>
          ) : null}
          <div
            className={cn(
              "mx-auto flex w-full max-w-3xl flex-col gap-4",
              !showEmptyGreeting && "px-3 py-3 sm:px-5 sm:py-5",
            )}
          >
            {messages.map((uiMessage) => {
              if (uiMessage.role === "user") {
                return (
                  <UserBubble
                    key={uiMessage.id}
                    message={uiMessage}
                    onOpenAttachment={handleOpenAttachment}
                  />
                );
              }
              if (uiMessage.role === "assistant") {
                return (
                  <AssistantBubble
                    key={uiMessage.id}
                    message={uiMessage}
                    busy={busy}
                    streaming={streaming}
                    showStatus={uiMessage.id === lastAssistantId}
                    busySince={busySince}
                    closedReviewId={
                      closedReviewId && reviewIdInMessage(uiMessage) === closedReviewId
                        ? closedReviewId
                        : null
                    }
                    onOpenCanvas={onOpenCanvas}
                    onRegenerate={onRegenerate}
                    activeCitation={activeCitation}
                    onCitationClick={setActiveCitation}
                    threadId={threadId}
                    onOpenDocument={handleOpenDocument}
                    feedbackRating={messageFeedback[uiMessage.id] ?? null}
                    onFeedbackChange={
                      onMessageFeedbackChange
                        ? (rating) => onMessageFeedbackChange(uiMessage.id, rating)
                        : undefined
                    }
                  />
                );
              }
              return null;
            })}
            {showPendingAttachPreview && pendingAttachPreview ? (
              <PendingUserAttachBubble preview={pendingAttachPreview} />
            ) : null}
            {showPendingPresence ? (
              <div className="mr-auto flex max-w-[85%] gap-2.5">
                <AssistantAvatar />
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  {latestLog ? null : attachmentUploading ? (
                    <div className={assistantBubbleClassName}>
                      <ThinkingLine label={PROCESSING_DOCUMENT_STATUS} since={busySince} />
                    </div>
                  ) : isPendingKnowledge ? (
                    <KnowledgeSearchActivityLog
                      query={null}
                      statusLabel="Searching your knowledge base…"
                      since={busySince}
                      active
                    />
                  ) : isPendingResearch ? (
                    <WebSearchActivityLog
                      query={null}
                      statusLabel="Searching the web…"
                      sources={[]}
                      since={busySince}
                      active
                    />
                  ) : (
                    <div className={assistantBubbleClassName}>
                      {streaming ? (
                        <TypingDots />
                      ) : (
                        <ThinkingLine label={LOOKUP_STATUS} since={busySince} />
                      )}
                    </div>
                  )}
                  {latestLog && progress ? <ReviewStepLog progress={progress} /> : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {showEmptyGreeting ? null : (
          <div className="shrink-0 px-3 pb-4 pt-1 sm:px-5">
            <div className="mx-auto w-full max-w-3xl">
              <SupervisorComposer
                busy={busy}
                docked
                compact={compactComposer}
                onSend={onSend}
                draftKey={draftKey}
                webSearchEnabled={webSearchEnabled}
                knowledgeSearchEnabled={knowledgeSearchEnabled}
                knowledgeScopeSelection={knowledgeScopeSelection}
                selectedScopeLibraries={selectedScopeLibraries}
                onWebSearchChange={onWebSearchChange}
                onKnowledgeSearchChange={onKnowledgeSearchChange}
                onKnowledgeScopeApply={onKnowledgeScopeApply}
                placeholder={composerPlaceholder}
              />
            </div>
          </div>
        )}
      </div>
      {activeDocument ? (
        <DocumentViewerPanel
          document={activeDocument}
          onClose={() => setActiveDocument(null)}
        />
      ) : activeCitation ? (
        <CitationPanel
          citation={activeCitation}
          threadId={threadId}
          onClose={() => setActiveCitation(null)}
          onOpenDocument={handleOpenDocument}
        />
      ) : null}
    </div>
  );
}
